// src/MainApp.tsx
import React, { useState, useEffect, FC } from "react";
import { Routes, Route, useParams } from "react-router-dom";

import Main from "./components/Main";
import Header from "./components/Header.tsx";
import Sidebar from "./components/Sidebar.tsx";

import DivisionPage from "./pages/DivisionPage";
import DivisionsDirectory from "./pages/DivisionsDirectory";
import NomenclatureDirectory from "./pages/NomenclatureDirectory";
import EquipmentDirectory from "./pages/EquipmentDirectory";
import EquipmentGroupDirectory from "./pages/EquipmentGroupDirectory";
import ProductSheet from "./pages/ProductSheet/ProductSheet";
import Orders from "./pages/OrdersPage";
import TechnologyCardDirectory from "./pages/TechnologyCardDirectory";
import ManualPlanningPage from "./pages/ManualPlanning/ManualPlanningPage";
import ResetPasswordPage from "./pages/ResetPasswordPage/ResetPasswordPage";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import UsersDirectory from "./pages/UsersDirectory";

import ProtectedRoute from "./PrivateRoute";
import { getEnterprise, getDivisions, getDivisionDetails } from "./api/enterprise";
import { EnterpriseProvider } from "./EnterpriseContext";
import { Toaster } from "react-hot-toast";

// Импорт наших стилей из MUI-styled файла
import {
  OuterContainer,
  MainContent,
  ContentSection
} from "./MainAppStyles.ts";

// Если хотите, можно описать интерфейс пропов MainApp, но кажется не нужно
const MainApp: FC = () => {
  const { enterpriseId } = useParams();
  const params = useParams();
  const productSheetId = params.productSheetId;

  const [enterprise, setEnterprise] = useState<any>(() => {
    const saved = localStorage.getItem("enterprise");
    return saved ? JSON.parse(saved) : null;
  });
  const [divisions, setDivisions] = useState<any[]>([]);
  const [selectedDivisionId, setSelectedDivisionId] = useState<string | null>(() =>
    localStorage.getItem("selectedDivisionId")
  );
  const [selectedDivisionName, setSelectedDivisionName] = useState<string | null>(() =>
    localStorage.getItem("selectedDivisionName")
  );

  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const toggleSidebar = () => setSidebarOpen(prev => !prev);

  useEffect(() => {
    async function fetchData() {
      if (!enterpriseId) {
        console.error("No enterpriseId found");
        return;
      }
      try {
        if (!enterprise || enterprise.enterprise_id !== enterpriseId) {
          const enterpriseData = await getEnterprise(enterpriseId);
          if (!enterprise || enterprise.enterprise_id !== enterpriseData.enterprise_id) {
            setEnterprise(enterpriseData);
            localStorage.setItem("enterprise", JSON.stringify(enterpriseData));
          }
        }
        if (selectedDivisionId && !selectedDivisionName) {
          const divisionData = await getDivisionDetails(selectedDivisionId);
          setSelectedDivisionName(divisionData.name);
          localStorage.setItem("selectedDivisionName", divisionData.name);
        }
        if (!divisions.length || (enterprise && enterprise.enterprise_id !== enterpriseId)) {
          const divisionsData = await getDivisions(enterpriseId);
          if (JSON.stringify(divisions) !== JSON.stringify(divisionsData)) {
            setDivisions(divisionsData);
          }
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    }
    fetchData();
  }, [enterpriseId, selectedDivisionId, selectedDivisionName, enterprise, divisions]);

  useEffect(() => {
    if (selectedDivisionId) {
      localStorage.setItem("selectedDivisionId", selectedDivisionId);
    }
  }, [selectedDivisionId]);

  if (!enterpriseId) {
    return <div>Ошибка: enterpriseId не найден</div>;
  }

  return (
    <EnterpriseProvider>
      <OuterContainer>
        {/* Сайдбар слева */}
        <Sidebar
          enterpriseId={enterpriseId}
          isLocked={!selectedDivisionId}
          isOpen={sidebarOpen}
          onToggle={toggleSidebar}
        />

        {/* Правая часть: Header + контент */}
        <MainContent>
          <Header
            enterprise={enterprise}
            divisions={divisions}
            selectedDivisionId={selectedDivisionId}
            selectedDivisionName={selectedDivisionName}
            setDivisions={setDivisions}
            setSelectedDivisionId={setSelectedDivisionId}
            setSelectedDivisionName={setSelectedDivisionName}
          />

          <ContentSection component="section">
            {/* <div className="container-fluid"> */}
              <Routes>
                <Route
                  index
                  element={
                    <ProtectedRoute>
                      <Main
                        enterpriseId={enterpriseId}
                        enterprise={enterprise}
                        divisions={divisions}
                        setDivisions={setDivisions}
                        setSelectedDivisionId={setSelectedDivisionId}
                        setSelectedDivisionName={setSelectedDivisionName}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="division/:divisionId"
                  element={
                    <ProtectedRoute>
                      <DivisionPage
                        enterprise={enterprise}
                        divisionId={selectedDivisionId}
                        divisions={divisions}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/divisions"
                  element={
                    <ProtectedRoute>
                      <DivisionsDirectory enterpriseId={enterpriseId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/nomenclature"
                  element={
                    <ProtectedRoute>
                      <NomenclatureDirectory enterpriseId={enterpriseId} />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/equipment"
                  element={
                    <ProtectedRoute>
                      <EquipmentDirectory
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/equipment-group"
                  element={
                    <ProtectedRoute>
                      <EquipmentGroupDirectory
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="product-sheet/:productSheetId"
                  element={
                    <ProtectedRoute>
                      <ProductSheet
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                        productSheetId={productSheetId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/technology-card"
                  element={
                    <ProtectedRoute>
                      <TechnologyCardDirectory
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/manual-planning"
                  element={
                    <ProtectedRoute>
                      <ManualPlanningPage
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="directory/users"
                  element={
                    <ProtectedRoute>
                      <UsersDirectory
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="orders"
                  element={
                    <ProtectedRoute>
                      <Orders
                        enterpriseId={enterpriseId}
                        divisionId={selectedDivisionId}
                      />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="reset-password"
                  element={<ResetPasswordPage enterpriseId={enterpriseId} />}
                />
                <Route
                  path="profile"
                  element={
                    <ProtectedRoute>
                      <ProfilePage enterpriseId={enterpriseId} />
                    </ProtectedRoute>
                  }
                />
              </Routes>
            {/* </div> */}
          </ContentSection>
        </MainContent>
      </OuterContainer>

      <Toaster position="top-right" reverseOrder={false} />
    </EnterpriseProvider>
  );
}

export default MainApp;
